import type { KeyframeProp, LayerKeyframes } from '../store/useAppStore';

/** Shared parameters for all maybeAutoKey variants. */
interface AutoKeyBase {
  layerId: string;
  autoKey: 0 | 1 | 2;
  currentFrame: number;
  layerKeyframes: Record<string, LayerKeyframes>;
  setKeyframe: (layerId: string, prop: KeyframeProp, frame: number, value: boolean | number) => void;
}

/**
 * Conditionally records a single keyframe based on the current auto-key mode.
 *
 * - mode 0: no-op
 * - mode 1: keys only if the channel already has at least one keyframe (or `forceKey` is true)
 * - mode 2: always keys
 *
 * `pushKfSnapshot` is called exactly once when any keyframe is about to be written,
 * so undo correctly reverts the entire auto-key operation as one step.
 */
export function maybeAutoKey(params: AutoKeyBase & {
  prop: KeyframeProp;
  value: boolean | number;
  /** Override mode-1 "must have existing keyframes" check (e.g. Shift-drag). */
  forceKey?: boolean;
}): void {
  const { layerId, prop, value, autoKey, currentFrame, layerKeyframes,
          setKeyframe, forceKey } = params;
  if (autoKey === 0) return;
  const kfs = layerKeyframes[layerId];
  if (!kfs) return;
  if (autoKey === 1 && !forceKey && Object.keys(kfs[prop]).length === 0) return;
  setKeyframe(layerId, prop, currentFrame, value);
}

/**
 * Conditionally records a batch of keyframes in a single undo step.
 * Used when multiple props change simultaneously (e.g. drag-rotate touches X, Y, Z).
 *
 * Each prop is independently tested against mode-1's "must have existing keyframes" rule.
 * `pushKfSnapshot` is called at most once regardless of how many props pass the filter.
 */
export function maybeAutoKeyBatch(params: AutoKeyBase & {
  props: ReadonlyArray<{ prop: KeyframeProp; value: boolean | number }>;
  forceKey?: boolean;
}): void {
  const { layerId, props, autoKey, currentFrame, layerKeyframes,
          setKeyframe, forceKey } = params;
  if (autoKey === 0) return;
  const kfs = layerKeyframes[layerId];
  if (!kfs) return;
  const toKey = props.filter(({ prop }) =>
    autoKey === 2 || forceKey || Object.keys(kfs[prop]).length > 0
  );
  if (!toKey.length) return;
  toKey.forEach(({ prop, value }) => setKeyframe(layerId, prop, currentFrame, value));
}
